import React from "react";
import { Link } from "react-router-dom";
import { Database, FileCheck2, LockKeyhole, Mail, Phone, ShieldCheck, UserCheck } from "lucide-react";
import MarketingHeader from "../components/marketing/MarketingHeader";
import Footer from "../components/marketing/Footer";

const dataCollected = [
  "Account details such as your name, email address, phone number, password, role, and account status.",
  "Student and guardian details such as gender, date of birth, parent or guardian name, and student ID.",
  "Academic details such as university, college, roll number, registration number, degree, department, semester, session, subject, domain, batch, and year of passing.",
  "Program details such as selected internship, mode, enrollment status, progress, attendance, assignments, and certificate eligibility.",
  "Payment details such as amount, currency, selected plan, coupon code, payment status, Razorpay order ID, payment ID, and verification status.",
  "Certificate records used to issue and verify digital certificates.",
];

const usage = [
  "Create and manage student accounts and admin accounts.",
  "Process registrations, enrollments, payments, coupons, and payment verification.",
  "Deliver internship programs, attendance tracking, assignments, progress updates, and certificates.",
  "Allow students and admins to view the information needed for their dashboard.",
  "Verify certificates publicly when a valid certificate ID is entered.",
  "Send support, operational, payment, enrollment, and certificate-related communication.",
  "Protect the platform from misuse, unauthorized access, duplicate records, and payment disputes.",
];

const flow = [
  {
    title: "Registration",
    text: "You enter personal, guardian, academic, subject, domain, program, and login details. We validate required fields before moving to payment.",
  },
  {
    title: "Payment",
    text: "For paid programs, Razorpay Checkout receives the payment amount and prefilled contact details needed to complete the transaction.",
  },
  {
    title: "Verification",
    text: "After a successful payment, the payment IDs and signature are verified through the configured backend when available.",
  },
  {
    title: "Account Creation",
    text: "Only after successful payment or a valid free coupon flow do we create the Firebase Auth user and save student, payment, and enrollment records.",
  },
  {
    title: "Program Use",
    text: "Admins may update attendance, assignments, enrollments, payments, courses, and certificates so students can track their internship progress.",
  },
  {
    title: "Certificate Verification",
    text: "Certificate details may be shown when someone uses the certificate verification page with a valid certificate ID.",
  },
];

const rights = [
  "Ask for a copy of your stored account and student data.",
  "Request correction of inaccurate details.",
  "Request deletion of your account data where legal, academic, payment, or certificate record obligations do not require retention.",
  "Contact us about payment, certificate, login, privacy, or data-access concerns.",
];

const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-slate-50">
    <MarketingHeader />

    <section className="bg-slate-900 text-white py-20">
      <div className="marketing-container max-w-4xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Privacy Policy</div>
        <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight">
          How Kampus Campanion handles student data.
        </h1>
        <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-3xl">
          This policy explains what information we collect, why we collect it, how it moves through the platform, and how you can contact us about privacy concerns.
        </p>
        <p className="mt-4 text-sm text-slate-400">Last updated: May 25, 2026</p>
      </div>
    </section>

    <section className="section-padding">
      <div className="marketing-container max-w-5xl space-y-10">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: ShieldCheck, title: "Purpose Limited", text: "We use student data to run registrations, learning workflows, payments, and certificates." },
            { icon: LockKeyhole, title: "Access Controlled", text: "Student dashboards, admin dashboards, and Firestore rules limit who can view or change records." },
            { icon: UserCheck, title: "Student Rights", text: "Students can contact us for access, correction, deletion, or privacy support requests." },
          ].map((item) => (
            <div key={item.title} className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
                <item.icon className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-heading font-semibold text-lg text-slate-900">{item.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{item.text}</p>
            </div>
          ))}
        </div>

        <PolicyBlock icon={Database} title="Information We Collect">
          <p>
            We collect information that you provide during registration, login, payment, course participation, and certificate generation. This may include:
          </p>
          <PolicyList items={dataCollected} />
        </PolicyBlock>

        <PolicyBlock icon={FileCheck2} title="How We Use Your Data">
          <p>
            We use your data only for platform operations, student support, compliance, and security-related purposes. This includes:
          </p>
          <PolicyList items={usage} />
        </PolicyBlock>

        <PolicyBlock icon={ShieldCheck} title="Privacy Flow">
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            {flow.map((item, index) => (
              <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Step {index + 1}</div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{item.text}</p>
              </div>
            ))}
          </div>
        </PolicyBlock>

        <PolicyBlock icon={LockKeyhole} title="Storage, Sharing, and Security">
          <p>
            Kampus Campanion uses Firebase Authentication and Firestore to manage users and student records. We use Razorpay to support payment checkout and verification. We do not sell personal data.
          </p>
          <p>
            We may share limited information with service providers when needed to operate the platform, process payments, maintain records, resolve support requests, comply with law, or protect the platform from misuse.
          </p>
          <p>
            We retain records for as long as needed for enrollment history, payment reconciliation, certificate verification, legal obligations, dispute handling, and platform operations.
          </p>
        </PolicyBlock>

        <PolicyBlock icon={UserCheck} title="Your Choices and Rights">
          <PolicyList items={rights} />
          <p>
            For security, we may need to verify your identity before acting on account, student record, or payment-related requests.
          </p>
        </PolicyBlock>

        <div className="bg-slate-900 text-white rounded-xl p-6 md:p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Contact Us</div>
          <h2 className="font-heading text-2xl font-bold">Privacy, data, and support contact</h2>
          <p className="text-slate-300 leading-relaxed mt-3 max-w-3xl">
            For privacy questions, data access or deletion requests, payment issues, certificate verification help, or general support, contact Kampus Campanion.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6 text-sm">
            <ContactItem icon={Mail} label="Email" value="support@kampuscompanion.com" />
            <ContactItem icon={Phone} label="Phone" value="+91 9217242305" />
            <ContactItem label="Address" value="Company Sarai, Rauza Road, Sasaram, Bihar - 821115" />
          </div>
          <Link to="/contact" className="inline-flex mt-6 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            Go to Contact Page
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const PolicyBlock = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-slate-900">{title}</h2>
    </div>
    <div className="space-y-4 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const PolicyList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item} className="flex gap-3">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0"></span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ContactItem = ({ icon: Icon, label, value }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-2 text-blue-300 font-semibold">
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </div>
    <div className="text-slate-200 mt-2 leading-relaxed">{value}</div>
  </div>
);

export default PrivacyPolicyPage;

